import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, Fragment as _Fragment, createBlock as _createBlock, withModifiers as _withModifiers, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "toolbar" }
const _hoisted_2 = { class: "upload-controls" }
const _hoisted_3 = { class: "workspace" }
const _hoisted_4 = ["src"]

import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { message } from 'ant-design-vue'
import Upload from '@/components/Upload.vue'

type Point = { x: number; y: number }
type AspectRatio = '3:4' | '1:1' | '9:16'


export default /*@__PURE__*/_defineComponent({
  __name: 'ImageTool',
  setup(__props) {

const imageUrl = ref('')
const analysisMode = ref<'auto' | 'manual'>('manual')
const canvasRef = ref<HTMLElement>()
const imageRef = ref<HTMLImageElement>()
const interaction = ref<'idle' | 'selecting' | 'panning'>('idle')
const pan = ref({ x: 0, y: 0 })
const scale = ref(1)
const dragStart = ref<Point | null>(null)
const selectionStart = ref<Point | null>(null)
const selectionEnd = ref<Point | null>(null)
const hoverPoint = ref<Point | null>(null)
const hoverPosition = ref<Point | null>(null)
const selection = ref<{ x: number; y: number; w: number; h: number } | null>(null)
const aspectRatio = ref<string | null>(null)
const actualBorderRatio = ref<string | null>(null)
const originalRatio = ref<string | null>(null)

const imageStyle = computed(() => ({
  transform: `translate(${pan.value.x}px, ${pan.value.y}px) scale(${scale.value})`,
}))

const selectionBox = computed(() => {
  if (!selectionStart.value || !selectionEnd.value || !imageRef.value) return null
  const imageWidth = imageRef.value.naturalWidth * scale.value
  const imageHeight = imageRef.value.naturalHeight * scale.value
  const start = {
    x: pan.value.x + selectionStart.value.x * imageWidth,
    y: pan.value.y + selectionStart.value.y * imageHeight,
  }
  const end = {
    x: pan.value.x + selectionEnd.value.x * imageWidth,
    y: pan.value.y + selectionEnd.value.y * imageHeight,
  }

  return {
    left: `${Math.min(start.x, end.x)}px`,
    top: `${Math.min(start.y, end.y)}px`,
    width: `${Math.abs(end.x - start.x)}px`,
    height: `${Math.abs(end.y - start.y)}px`,
  }
})

const coordinateTipStyle = computed(() => ({
  left: `${(hoverPosition.value?.x ?? 0) + 12}px`,
  top: `${(hoverPosition.value?.y ?? 0) + 12}px`,
}))

const selectionCoordinates = computed(() => {
  if (!selection.value) return null
  const x = truncateCoordinate(selection.value.x)
  const y = truncateCoordinate(selection.value.y)
  const w = Math.min(truncateCoordinate(selection.value.w), maximumDisplaySize(x))
  const h = Math.min(truncateCoordinate(selection.value.h), maximumDisplaySize(y))

  return {
    x: formatCoordinate(x),
    y: formatCoordinate(y),
    w: formatCoordinate(w),
    h: formatCoordinate(h),
  }
})

watch(imageUrl, () => {
  clearSelection()
  hoverPoint.value = null
  originalRatio.value = null
})

function fitImage() {
  if (!canvasRef.value || !imageRef.value) return
  const { clientWidth, clientHeight } = canvasRef.value
  originalRatio.value = simplifyRatio(imageRef.value.naturalWidth, imageRef.value.naturalHeight)
  scale.value = Math.min(1, clientWidth / imageRef.value.naturalWidth, clientHeight / imageRef.value.naturalHeight)
  pan.value = {
    x: (clientWidth - imageRef.value.naturalWidth * scale.value) / 2,
    y: (clientHeight - imageRef.value.naturalHeight * scale.value) / 2,
  }
  if (analysisMode.value === 'auto') void analyzeWhiteArea()
}

function handleModeChange() {
  if (analysisMode.value === 'auto') {
    void analyzeWhiteArea()
  } else {
    clearSelection()
    fitImage()
  }
}

async function analyzeWhiteArea() {
  if (!imageRef.value || !imageRef.value.complete || !imageRef.value.naturalWidth) {
    message.warning('请先上传完成图片后再进行自动解析')
    return
  }

  const source = imageRef.value
  const maxScanSize = 1200
  const scanScale = Math.min(1, maxScanSize / Math.max(source.naturalWidth, source.naturalHeight))
  const width = Math.max(1, Math.round(source.naturalWidth * scanScale))
  const height = Math.max(1, Math.round(source.naturalHeight * scanScale))
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d', { willReadFrequently: true })
  if (!context) return

  let pixels: Uint8ClampedArray
  try {
    context.drawImage(source, 0, 0, width, height)
    pixels = context.getImageData(0, 0, width, height).data
  } catch {
    message.error('图片像素读取失败，无法自动解析')
    return
  }

  const total = width * height
  const visited = new Uint8Array(total)
  const queue = new Int32Array(total)
  const minPixels = Math.max(64, Math.floor(total * 0.002))
  const ratios: Array<{ label: AspectRatio; value: number }> = [
    { label: '3:4', value: 3 / 4 },
    { label: '1:1', value: 1 },
    { label: '9:16', value: 9 / 16 },
  ]
  let bestMatch: { x: number; y: number; w: number; h: number; area: number; ratio: AspectRatio; error: number } | null = null

  const isTransparent = (index: number) => {
    const offset = index * 4
    return pixels[offset + 3] <= 5
  }

  for (let index = 0; index < total; index += 1) {
    if (visited[index] || !isTransparent(index)) continue

    let head = 0
    let tail = 0
    let area = 0
    let minX = index % width
    let maxX = minX
    let minY = Math.floor(index / width)
    let maxY = minY
    visited[index] = 1
    queue[tail++] = index

    while (head < tail) {
      const current = queue[head++]
      const x = current % width
      const y = Math.floor(current / width)
      area += 1
      minX = Math.min(minX, x)
      maxX = Math.max(maxX, x)
      minY = Math.min(minY, y)
      maxY = Math.max(maxY, y)

      const above = current - width
      const below = current + width
      if (y > 0 && !visited[above] && isTransparent(above)) { visited[above] = 1; queue[tail++] = above }
      if (y < height - 1 && !visited[below] && isTransparent(below)) { visited[below] = 1; queue[tail++] = below }
      if (x > 0 && !visited[current - 1] && isTransparent(current - 1)) { visited[current - 1] = 1; queue[tail++] = current - 1 }
      if (x < width - 1 && !visited[current + 1] && isTransparent(current + 1)) { visited[current + 1] = 1; queue[tail++] = current + 1 }
    }

    if (area < minPixels) continue
    const componentWidth = maxX - minX + 1
    const componentHeight = maxY - minY + 1
    const componentRatio = componentWidth / componentHeight
    const matchedRatio = ratios.reduce((closest, ratio) => (
      Math.abs(componentRatio - ratio.value) < Math.abs(componentRatio - closest.value) ? ratio : closest
    ))
    const ratioError = Math.abs(componentRatio - matchedRatio.value) / matchedRatio.value
    if (!bestMatch || area > bestMatch.area) {
      bestMatch = {
        x: minX / width,
        y: minY / height,
        w: componentWidth / width,
        h: componentHeight / height,
        area,
        ratio: matchedRatio.label,
        error: ratioError,
      }
    }
  }

  if (!bestMatch) {
    clearSelection()
    message.warning('未找到可识别的连续透明区域')
    return
  }

  selectionStart.value = { x: bestMatch.x, y: bestMatch.y }
  selectionEnd.value = { x: bestMatch.x + bestMatch.w, y: bestMatch.y + bestMatch.h }
  updateSelection(selectionStart.value, selectionEnd.value)
  aspectRatio.value = bestMatch.ratio
  if (bestMatch.error > 0.01) {
    message.warning(`已完成识别；真实边框比与 ${bestMatch.ratio} 的误差为 ${(bestMatch.error * 100).toFixed(2)}%`)
  } else {
    message.success(`已识别 ${bestMatch.ratio} 的透明区域`)
  }
}

function startInteraction(event: MouseEvent) {
  if (analysisMode.value !== 'manual') return
  if (!imageRef.value || !pointInImage(event)) return
  event.preventDefault()
  dragStart.value = { x: event.clientX, y: event.clientY }

  if (event.button === 2) {
    interaction.value = 'panning'
  } else if (event.button === 0) {
    interaction.value = 'selecting'
    const point = pointInImage(event)
    selectionStart.value = point
    selectionEnd.value = point
    updateSelection(point, point)
  }
  window.addEventListener('mousemove', moveInteraction)
  window.addEventListener('mouseup', endInteraction)
}

function moveInteraction(event: MouseEvent) {
  if (!imageRef.value || !canvasRef.value) return

  if (interaction.value === 'panning' && dragStart.value) {
    pan.value = {
      x: pan.value.x + event.clientX - dragStart.value.x,
      y: pan.value.y + event.clientY - dragStart.value.y,
    }
    dragStart.value = { x: event.clientX, y: event.clientY }
    return
  }

  const point = pointInImage(event)
  if (interaction.value === 'selecting' && selectionStart.value) {
    const end = point ?? clampToImage(event)
    selectionEnd.value = end
    updateSelection(selectionStart.value, end)
    return
  }

  hoverPoint.value = point
  if (point) {
    const canvasRect = canvasRef.value.getBoundingClientRect()
    hoverPosition.value = { x: event.clientX - canvasRect.left, y: event.clientY - canvasRect.top }
  }
}

function endInteraction() {
  interaction.value = 'idle'
  dragStart.value = null
  window.removeEventListener('mousemove', moveInteraction)
  window.removeEventListener('mouseup', endInteraction)
}

function clearHover() {
  if (interaction.value === 'idle') hoverPoint.value = null
}

function pointInImage(event: MouseEvent): Point | null {
  if (!imageRef.value) return null
  const rect = imageRef.value.getBoundingClientRect()
  if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) return null
  return { x: (event.clientX - rect.left) / rect.width, y: (event.clientY - rect.top) / rect.height }
}

function clampToImage(event: MouseEvent): Point {
  if (!imageRef.value) return { x: 0, y: 0 }
  const rect = imageRef.value.getBoundingClientRect()
  return {
    x: clamp((event.clientX - rect.left) / rect.width),
    y: clamp((event.clientY - rect.top) / rect.height),
  }
}

function updateSelection(start: Point, end: Point) {
  aspectRatio.value = calculateFrameRatio(start, end)
  actualBorderRatio.value = calculateActualFrameRatio(start, end)
  selection.value = {
    x: Math.min(start.x, end.x),
    y: Math.min(start.y, end.y),
    w: Math.abs(end.x - start.x),
    h: Math.abs(end.y - start.y),
  }
}

function calculateActualFrameRatio(start: Point, end: Point) {
  if (!imageRef.value) return null
  const width = Math.abs(end.x - start.x) * imageRef.value.naturalWidth
  const height = Math.abs(end.y - start.y) * imageRef.value.naturalHeight
  return width && height ? `${formatCoordinate(width / height)}:1` : null
}

function calculateFrameRatio(start: Point, end: Point) {
  if (!imageRef.value) return null
  const width = Math.round(Math.abs(end.x - start.x) * imageRef.value.naturalWidth)
  const height = Math.round(Math.abs(end.y - start.y) * imageRef.value.naturalHeight)
  return width && height ? simplifyRatio(width, height) : null
}

function clearSelection() {
  selectionStart.value = null
  selectionEnd.value = null
  selection.value = null
  aspectRatio.value = null
  actualBorderRatio.value = null
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    clearSelection()
    endInteraction()
  }
}

function clamp(value: number) {
  return Math.min(1, Math.max(0, value))
}

function formatCoordinate(value: number) {
  const truncatedValue = Math.floor(Math.max(0, value) * 100000)
  const integerPart = Math.floor(truncatedValue / 100000)
  const decimalPart = String(truncatedValue % 100000).padStart(5, '0')
  return `${integerPart}.${decimalPart}`
}

function truncateCoordinate(value: number) {
  return Math.floor(Math.max(0, value) * 100000) / 100000
}

function maximumDisplaySize(start: number) {
  // 预留一个最小精度单位，确保截尾后的起点和尺寸严格小于 1。
  return Math.max(0, truncateCoordinate(1 - start - 0.00001))
}

function simplifyRatio(width: number, height: number) {
  const divisor = greatestCommonDivisor(width, height)
  return `${width / divisor}:${height / divisor}`
}

function greatestCommonDivisor(a: number, b: number): number {
  return b === 0 ? a : greatestCommonDivisor(b, a % b)
}

onMounted(() => window.addEventListener('keydown', handleKeydown))
onBeforeUnmount(() => {
  endInteraction()
  window.removeEventListener('keydown', handleKeydown)
})

return (_ctx: any,_cache: any) => {
  const _component_a_radio = _resolveComponent("a-radio")!
  const _component_a_radio_group = _resolveComponent("a-radio-group")!
  const _component_a_empty = _resolveComponent("a-empty")!
  const _component_a_descriptions_item = _resolveComponent("a-descriptions-item")!
  const _component_a_descriptions = _resolveComponent("a-descriptions")!
  const _component_a_card = _resolveComponent("a-card")!

  return (_openBlock(), _createBlock(_component_a_card, { title: "图片工具" }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(Upload, {
            "image-url": imageUrl.value,
            "onUpdate:imageUrl": _cache[0] || (_cache[0] = ($event: any) => ((imageUrl).value = $event)),
            "max-size": 10 * 1024 * 1024
          }, null, 8, ["image-url"]),
          _createVNode(_component_a_radio_group, {
            value: analysisMode.value,
            "onUpdate:value": _cache[1] || (_cache[1] = ($event: any) => ((analysisMode).value = $event)),
            onChange: handleModeChange
          }, {
            default: _withCtx(() => [
              _createVNode(_component_a_radio, { value: "auto" }, {
                default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                  _createTextVNode("自动解析", -1)
                ]))]),
                _: 1
              }),
              _createVNode(_component_a_radio, { value: "manual" }, {
                default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                  _createTextVNode("人工选择", -1)
                ]))]),
                _: 1
              })
            ]),
            _: 1
          }, 8, ["value"])
        ]),
        _cache[5] || (_cache[5] = _createElementVNode("span", { class: "hint" }, "请上传图片(10m内)。左键拖动框选区域，右键拖动图片；坐标为 0～1 的相对值。", -1))
      ]),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", {
          ref_key: "canvasRef",
          ref: canvasRef,
          class: _normalizeClass(["canvas", { 'is-panning': interaction.value === 'panning' }]),
          onContextmenu: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["prevent"])),
          onMousedown: startInteraction,
          onMousemove: moveInteraction,
          onMouseleave: clearHover
        }, [
          (imageUrl.value)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _createElementVNode("img", {
                  ref_key: "imageRef",
                  ref: imageRef,
                  src: imageUrl.value,
                  crossorigin: "anonymous",
                  class: "canvas-image",
                  style: _normalizeStyle(imageStyle.value),
                  draggable: "false",
                  onLoad: fitImage
                }, null, 44, _hoisted_4),
                (selectionBox.value)
                  ? (_openBlock(), _createElementBlock("div", {
                      key: 0,
                      class: "selection",
                      style: _normalizeStyle(selectionBox.value)
                    }, null, 4))
                  : _createCommentVNode("", true),
                (hoverPoint.value && interaction.value !== 'selecting')
                  ? (_openBlock(), _createElementBlock("div", {
                      key: 1,
                      class: "coordinate-tip",
                      style: _normalizeStyle(coordinateTipStyle.value)
                    }, " x: " + _toDisplayString(formatCoordinate(hoverPoint.value.x)) + ", y: " + _toDisplayString(formatCoordinate(hoverPoint.value.y)), 5))
                  : _createCommentVNode("", true)
              ], 64))
            : (_openBlock(), _createBlock(_component_a_empty, {
                key: 1,
                description: "请先上传图片"
              }))
        ], 34),
        _createVNode(_component_a_card, {
          size: "small",
          title: "选区相对坐标",
          class: "coordinate-panel"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_a_descriptions, {
              column: 1,
              size: "small"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_a_descriptions_item, { label: "X" }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(selectionCoordinates.value?.x ?? '--'), 1)
                  ]),
                  _: 1
                }),
                _createVNode(_component_a_descriptions_item, { label: "Y" }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(selectionCoordinates.value?.y ?? '--'), 1)
                  ]),
                  _: 1
                }),
                _createVNode(_component_a_descriptions_item, { label: "W" }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(selectionCoordinates.value?.w ?? '--'), 1)
                  ]),
                  _: 1
                }),
                _createVNode(_component_a_descriptions_item, { label: "H" }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(selectionCoordinates.value?.h ?? '--'), 1)
                  ]),
                  _: 1
                }),
                _createVNode(_component_a_descriptions_item, { label: "边框比" }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(aspectRatio.value ?? '--'), 1)
                  ]),
                  _: 1
                }),
                _createVNode(_component_a_descriptions_item, { label: "真实边框比" }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(actualBorderRatio.value ?? '--'), 1)
                  ]),
                  _: 1
                }),
                _createVNode(_component_a_descriptions_item, { label: "原图比例" }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(originalRatio.value ?? '--'), 1)
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ])
    ]),
    _: 1
  }))
}
}

})